// LayoutEditorInspector.jsx

import React, { useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './LayoutEditorInspector.css';

// Components
import LayoutEditorInspectorPreview from './LayoutEditorInspectorPreview';
import { buildLayoutInspectorSections } from './layoutInspectorActions';
import Button from 'components/button/Button';

/**
 * LayoutEditorInspector
 *
 * Persistent labeled controls for the active layout editor selection.
 *
 * @returns {ReactNode} The rendered component.
 */
const LayoutEditorInspector = ({
  inspectorPreview = null,
  toolbarContext = null,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- MEMOS ----- //

  const sections = useMemo(() => (
    buildLayoutInspectorSections(toolbarContext)
  ), [toolbarContext]);

  const showPreview = Boolean(inspectorPreview?.enabled);

  // ----- RENDER ----- //

  return (
    <aside
      className="layout-editor-inspector"
      style={{
        borderLeft: `1px solid ${theme.outlineMuted}`,
      }}
      onMouseDownCapture={(event) => {
        event.preventDefault();
      }}
    >

      {/* HEADER */}
      <div
        className="layout-editor-inspector-header"
      >
        Inspector
      </div>

      {/* BODY */}
      <div className="layout-editor-inspector-body">
        {showPreview ? (
          <LayoutEditorInspectorPreview
            emptyMessage={inspectorPreview.emptyMessage}
            loading={inspectorPreview.loading}
            records={inspectorPreview.records}
            selectedRecordKey={inspectorPreview.selectedRecordKey}
            typeDef={inspectorPreview.typeDef}
            onSelectRecord={inspectorPreview.onSelectRecord}
          />
        ) : null}

        {/* SECTION */}
        <section className="layout-editor-inspector-section layout-editor-inspector-toolbox">

          {/* SECTION LABEL */}
          <div
            className="layout-editor-inspector-section-label"
          >
            Toolbox
          </div>

          {!sections.length ? (
            <div
              className="layout-editor-inspector-empty"
            >
              Select a row or block on the canvas to edit layout structure and content.
            </div>
          ) : (
            <div className="layout-editor-inspector-sections">
              {sections.map((section) => (
                <section
                  className="layout-editor-inspector-section"
                  key={section.key}
                >

                  {/* SECTION LABEL */}
                  <div
                    className="layout-editor-inspector-section-label"
                  >
                    {section.label}
                  </div>

                  {/* ACTIONS */}
                  <div className="layout-editor-inspector-actions">
                    {section.actions.map((action) => (
                      <Button
                        key={`${section.key}-${action.label}`}
                        backgroundColor="transparent"
                        hoverBackgroundColor={theme.buttonHoverBackgroundColor}
                        borderColor={theme.outlineMuted}
                        borderRadius={8}
                        fontSize="13px"
                        foregroundColor={action.destructive ? theme.highlight : theme.foreground}
                        height="36px"
                        iconKey={action.iconKey || undefined}
                        label={action.label}
                        title={action.title}
                        onClick={action.onClick}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </section>
      </div>
    </aside>
  );
};

// Dev Tools
LayoutEditorInspector.displayName = 'LayoutEditorInspector';

export default LayoutEditorInspector;
